import { useQuery } from "react-query";

import axios, { AxiosError, AxiosResponse } from "axios";

import { VIS_SERVICE_ADDRESS } from "../../../constants/app.constants";
import { ThreeDViewerOptionT } from "../../../models";

export const GET_3D_SCENE_QUERY_KEY = "3dScene";

/**
 * How long one answer stays good for, and how long it is kept after the last
 * viewer stops asking for it. A scene is a pure function of the body that
 * asked for it, so a repeat of the same body inside the window is answered
 * from the cache instead of going back to the service. Long enough that a
 * second viewer mounting beside the first never posts again; short enough
 * that a session of option toggling cannot fill memory with dead scenes.
 */
export const GET_3D_SCENE_STALE_TIME = 60_000;
export const GET_3D_SCENE_CACHE_TIME = 60_000;

export type Get3DSceneRequestT = {
  selectedWells;
  options: ThreeDViewerOptionT;
};

/**
 * The 3D scene for one request body.
 *
 * This was a mutation, so every mounted viewer posted its own copy: a well
 * card open beside a 3D widget asked the vis service for the same scene twice
 * at once, and the loser came back as an error the viewer showed as "Unable to
 * render". Keying a query on the body instead is what `useMapEntitiesQuery`
 * and the `entitiesOnMap` query in `ThreeDViewer` already do for the same
 * reason - react-query gives both subscribers one in-flight request and one
 * cache entry.
 *
 * Pass nothing to ask for nothing.
 */
export default function useGet3DScene(request: Maybe<Get3DSceneRequestT>) {
  const body = request ? { data: request.selectedWells, options: request.options } : null;

  /*
   * The key is the body axios will put on the wire: `JSON.stringify` drops the
   * callbacks hanging off `options` exactly as the POST does, so two viewers
   * that would send the same bytes land on the same key. Two that would not -
   * different sources, different scale - keep separate entries, which is the
   * behaviour they had before.
   */
  const serializedBody = body ? JSON.stringify(body) : "";

  return useQuery<AxiosResponse, AxiosError>(
    [GET_3D_SCENE_QUERY_KEY, serializedBody],
    /*
     * Reading `signal` is what tells react-query this request can be aborted.
     * It aborts when the last subscriber leaves the key - a viewer whose
     * inputs changed mid-flight, or one that unmounted - so a second viewer
     * still waiting on the same scene keeps its request.
     */
    ({ signal }) => axios.post(`${VIS_SERVICE_ADDRESS}/3d/scene`, body, { signal }),
    {
      enabled: !!body && Object.keys(request.selectedWells ?? {}).length > 0,
      // The caller shows the service's own message, so it must not sit behind
      // three silent retries first.
      retry: false,
      staleTime: GET_3D_SCENE_STALE_TIME,
      cacheTime: GET_3D_SCENE_CACHE_TIME
    }
  );
}
